import { useDisclosure } from "@mantine/hooks";
import { Text, Portal, Loader, ActionIcon } from "@mantine/core";
import { Flex } from "../../../styled";
import { Button } from "../../../components/shared/Button/Button";
import { useDispatch, useSelector } from "../../../redux/hooks";
import { Fragment, useEffect, useMemo, useState, useRef } from "react";
import {
  useGetApi,
  useIsNewEntry,
  useIsOnline,
  usePostApi,
  useTheme,
} from "../../../hooks";
import { useParams } from "react-router-dom";
import { getYachtId, crumb } from "../../../utils";
import {
  getOnlyNMEARows,
  INMEARow,
  INMEASection,
  parseNMEADataForSaving,
  parseNMEASections,
  cleanupInvalidNMEASelections,
} from "./NMEAAutomationModal.utils";
import {
  setNMEAData,
  setSections,
  sortSectionsByTime,
  addNewDynamicRow,
} from "../../../redux/slices/generatorSlice";
import {
  StyledNMEAModalContainer,
  StyledNMEAOverlay,
} from "./NMEAAutomationModal.styled";
import { useFormContext } from "react-hook-form";
import { setNMEAModalData } from "../../../redux/slices/generatorFeaturesSlice";

import { Checkbox } from "@mantine/core";
import { insertValues } from "../../../redux/utils/insertValues";
import { useTriggerAutoSave } from "../../hooks/useTriggerAutoSave";
import NMEAAutomationModalPreview from "./NMEAAoutomationModalPreview";
import { IconX, IconGps } from "@tabler/icons-react";
import { ICol } from "../../../interfaces";
import { useNMEALocation } from "./useNMEALocation";
import { getRowWithExclusives } from "../../../utils/logbookUtils/getRowWithExclusives";
import { clearRow } from "../../utils/dynamicRows";

interface Props {
  containerRef: React.RefObject<HTMLDivElement> | null;
}

const mapper = (data: any): Record<string, string>[] => data.data;

const NMEAAutomationModal = ({ containerRef }: Props) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const [opened, { open, close }] = useDisclosure();
  const { setValue, getValues } = useFormContext();
  const { lbeidOrPartId, logbookId } = useParams();
  const { NMEAModalData } = useSelector(s => s.generatorFeaturesSlice);
  const { lbentry, entryDataToSave, sortOrder } = useSelector(
    s => s.generatorSlice,
  );
  const [loading, setLoading] = useState<false | "auto" | "manual">();
  const isNewEntry = useIsNewEntry();
  const { post } = usePostApi();
  const triggerAutoSave = useTriggerAutoSave();
  const isOnline = useIsOnline();
  const [selectedSections, setSelectedSections] = useState<number[][]>([]);
  const id = useSelector(state => state.generatorSlice.latestEntry.id);
  const {
    anchorChecked,
    underwayChecked,
    location,
    setAnchorChecked,
    setUnderwayChecked,
  } = useNMEALocation("underway");

  // Function to check if an NMEA row still corresponds to an existing logbook row
  const isNMEARowStillValid = (
    nmeaId: string,
    sectionIndex: number,
  ): boolean => {
    return isNMEARowStillValidForSections(
      nmeaId,
      sectionIndex,
      lbentry.sections,
    );
  };

  // Helper function to check if an NMEA row still corresponds to an existing logbook row in given sections
  const isNMEARowStillValidForSections = (
    nmeaId: string,
    sectionIndex: number,
    sections: typeof lbentry.sections,
  ): boolean => {
    if (!sections[sectionIndex]?.rows) return false;

    // Check if any row in the section has a column with this NMEA ID
    return sections[sectionIndex].rows.some(row => {
      if (!row.cols) return false;
      return Object.values(row.cols).some(col => {
        const typedCol = col as ICol;
        return typedCol.NMEAId === nmeaId;
      });
    });
  };

  // Track initial location to detect changes
  const initialLocationRef = useRef<string | null>(null);
  // Capture and show the value of entryDataToSave?.latest === id only when id is first defined (not undefined/null/empty)
  const tempIsNewEntryRef = useRef<
    { value: boolean | undefined; checked: string | undefined } | undefined
  >(undefined);
  if (
    tempIsNewEntryRef.current === undefined &&
    id !== undefined &&
    id !== null &&
    String(id).length > 0
  ) {
    tempIsNewEntryRef.current = {
      value: entryDataToSave?.latest === id,
      checked: `${entryDataToSave?.latest} === ${id}`,
    };
  }
  const tempIsNewEntry = tempIsNewEntryRef.current?.value ?? false;
  const isSophia = getYachtId() === "a810afbe-3e95-5729-1e8d-1bb8306fd3f0";

  // Wait until id is not undefined before constructing nmeaApiBody
  const nmeaApiBody = useMemo(() => {
    if (id === undefined || id === null || String(id).length === 0)
      return undefined;
    return {
      params: {
        yachtid: getYachtId(),
        lbid: logbookId,
        ...((NMEAModalData?.triggeredBy === "refresh"
          ? isNewEntry
          : tempIsNewEntry) && { newEntry: true }),
      },
    };
  }, [tempIsNewEntry, logbookId, id, NMEAModalData?.triggeredBy]);

  const { data, refetch, isLoading, setData } = useGetApi({
    request: "get-NMEA-data",
    body: nmeaApiBody,
    mapper,
    fetchOnInit: false,
  });

  useEffect(() => {
    if (
      id === undefined ||
      id === null ||
      String(id).length === 0 ||
      isLoading ||
      !isOnline ||
      !NMEAModalData?.opened ||
      !entryDataToSave?.latest
    ) {
      return;
    }
    if (lbentry.NMEA) {
      if (
        !lbentry.NMEAData ||
        !lbentry.NMEAData.length ||
        NMEAModalData?.triggeredBy
      ) {
        const params =
          NMEAModalData.rowIndex !== undefined
            ? { date: entryDataToSave.entrydate }
            : {};
        refetch({
          params,
        });
      } else {
        // Clean up any invalid selections before processing
        const cleanedNMEAData = cleanupInvalidNMEASelections(
          lbentry.NMEAData,
          lbentry.sections,
        );

        // Initialize selectedSections array with proper indices based on originalSectionIndex
        const newSelectedSections: number[][] = [];

        const flatData: Record<string, string>[] = (
          cleanedNMEAData || []
        ).flatMap((section: INMEASection) =>
          section.rows.flatMap((r, index): any => {
            if (!r) return [];
            // Use the cleaned selection state
            const shouldBeSelected = r.selected ?? false;

            const newRow: Record<string, string | number | boolean> = {
              _originalSectionIndex: section.originalSectionIndex,
              _NMEAId: r.NMEAId,
              _wasSelected: shouldBeSelected,
            };

            if (shouldBeSelected) {
              // Initialize the array for this section if it doesn't exist
              if (!newSelectedSections[section.originalSectionIndex]) {
                newSelectedSections[section.originalSectionIndex] = [];
              }
              newSelectedSections[section.originalSectionIndex] = [
                ...newSelectedSections[section.originalSectionIndex],
                index,
              ];
            }

            let hasTimeCol = false;
            Object.entries(r.cols).forEach(([NMEAKey, col]) => {
              newRow[NMEAKey] = col.value as string;
              if (NMEAKey === "_ROW_DATE_TIME") {
                hasTimeCol = true;
              }
            });

            const dateTimeObj = {
              _ROW_DATE_TIME: r.dateTime || "",
              _ROW_DATE_TIME_UTC: r.dateTimeUTC || "",
              _ROW_DATE_TIME_LT: r.dateTimeLT || "",
              _ROW_TIME_UTC: r.timeUTC || "",
              _ROW_TIME_LT: r.timeLT || "",
            };

            return { ...newRow, ...(hasTimeCol ? dateTimeObj : {}) };
          }),
        );

        setData?.(prev => {
          return flatData;
        });
        setSelectedSections(newSelectedSections);
      }
      open();
    } else {
      close();
    }
  }, [lbentry, NMEAModalData, opened, entryDataToSave, isOnline]);

  // Capture initial location when modal opens
  useEffect(() => {
    if (opened && initialLocationRef.current === null) {
      initialLocationRef.current = location;
    }
    if (!opened) {
      initialLocationRef.current = null;
    }
  }, [opened, location]);

  const handleClose = (
    dataWasAccepted?: boolean,
    updatedSections?: typeof lbentry.sections,
    options?: { skipRecordNMEAData?: boolean },
  ) => {
    // Check for location changes and record history
    const currentLocation = location;
    const initialLocation = initialLocationRef.current;

    if (
      initialLocation &&
      currentLocation &&
      initialLocation !== currentLocation
    ) {
      recordHistory(`Location change to ${currentLocation}`);
    }

    if (!options?.skipRecordNMEAData) {
      recordNMEAData(dataWasAccepted, updatedSections);
    }
    dispatch(setNMEAModalData(null));
    close();
    setSelectedSections([]);
    // dispatch(sortSectionsByTime(NMEAModalData?.sectionIndex));
  };

  useEffect(() => {
    return () => {
      dispatch(setNMEAModalData(null));
      close();
    };
  }, []);

  const recordHistory = async (action: string) => {
    if (!isOnline) return;
    await post({
      request: "record-history-action",
      body: {
        params: {
          yachtid: getYachtId(),
          lbid: logbookId,
          lbeid: lbeidOrPartId,
          action,
        },
      },
      shouldRunAfterAutosave: true,
    });
  };

  const findSelectedSections = (dataWasAccepted: boolean) => {
    const filteredData: INMEASection[] = [];
    previewSections.forEach((s, i) => {
      let newRows: INMEARow[] = [];
      newRows = s.rows.map((r, rowIdx) => ({
        ...r,
        selected: selectedSections[s.originalSectionIndex]?.includes(rowIdx),
      }));

      filteredData.push({
        ...s,
        rows: newRows,
      });
    });
    return filteredData;
  };

  const recordNMEAData = (
    dataWasAccepted?: boolean,
    updatedSections?: typeof lbentry.sections,
  ) => {
    const sectionsWithSelectedProp = findSelectedSections(
      dataWasAccepted ?? false,
    );

    // Use the updated sections if provided, otherwise use the current sections
    const sectionsToUse = updatedSections || lbentry.sections;

    // Clean up invalid selections from existing NMEA data first
    const cleanedExistingNMEAData = cleanupInvalidNMEASelections(
      lbentry.NMEAData,
      sectionsToUse,
    );

    // Clean up invalid selections from current selections
    const cleanedSectionsWithSelectedProp = sectionsWithSelectedProp.map(
      section => ({
        ...section,
        rows: section.rows.map(row => ({
          ...row,
          selected:
            row.selected &&
            isNMEARowStillValidForSections(
              row.NMEAId,
              section.originalSectionIndex,
              sectionsToUse,
            ),
        })),
      }),
    );

    const newNMEAData = parseNMEADataForSaving({
      sections: sectionsToUse,
      sectionsWithSelectedProp: cleanedSectionsWithSelectedProp,
      NMEAData: cleanedExistingNMEAData,
      NMEAModalData,
    });

    dispatch(setNMEAData(newNMEAData));
  };

  const handleAccept = async () => {
    const startTime = Date.now();

    // Log NMEA automation acceptance
    crumb("NMEA Modal: Automated data accepted", {
      triggeredBy: NMEAModalData?.triggeredBy,
      isNewEntry: tempIsNewEntry,
      entryId: lbeidOrPartId ?? id,
      logbookId,
      sectionIndex: NMEAModalData?.sectionIndex,
      rowIndex: NMEAModalData?.rowIndex,
      selectedSectionsCount: findSelectedSections(true).length,
    });

    await recordHistory("Automated Data Accepted");
    const filledSections = insertValues(getValues(), lbentry.sections);
    const newSections = parseNMEASections({
      originalSections: filledSections,
      newData: data,
      setValue,
      rowIndex: NMEAModalData?.rowIndex,
      sectionIndex: NMEAModalData?.sectionIndex,
      selectedSections: findSelectedSections(true),
      sortOrder,
      ...(isSophia && { location }),
    });

    const duration = Date.now() - startTime;
    crumb("NMEA Modal: Data processing completed", {
      hourlyRowsCountBeforeNMEA: filledSections[1]?.rows?.length,
      hourlyRowsCountAfterNMEA: newSections[1]?.rows?.length,
      duration,
      sectionsProcessed: newSections.length,
      success: true,
    });

    dispatch(setSections(newSections));
    handleClose(true, newSections);
    triggerAutoSave();
  };

  const handleEnterManually = async () => {
    // Add the prepared row for manual entry
    if (
      NMEAModalData?.pendingRow &&
      NMEAModalData?.sectionIndex !== undefined
    ) {
      dispatch(
        addNewDynamicRow({
          row: NMEAModalData.pendingRow,
          sectionIndex: NMEAModalData.sectionIndex,
          skipSorting:
            lbentry.sections[NMEAModalData.sectionIndex]?.NMEA_button,
        }),
      );
    }

    handleClose(undefined, undefined, { skipRecordNMEAData: true });
    void recordHistory("Data Entered Manually");
    triggerAutoSave();
  };

  const previewSections: INMEASection[] = useMemo(() => {
    if (!data || !opened) return [];
    const filledSections = parseNMEASections({
      originalSections: lbentry.sections,
      newData: data,
      rowIndex: NMEAModalData?.rowIndex,
      sectionIndex: NMEAModalData?.sectionIndex,
      sortOrder,
    });
    return getOnlyNMEARows(filledSections, data, NMEAModalData?.sectionIndex);
  }, [lbentry.sections, data, isNewEntry, NMEAModalData, opened, sortOrder]);

  if (!containerRef?.current || !opened) {
    return null;
  }

  return (
    <Portal target={containerRef.current}>
      <StyledNMEAOverlay onClick={() => handleClose()}>
        <StyledNMEAModalContainer
          theme={theme}
          onClick={e => e.stopPropagation()}>
          <Flex justify="space-between">
            <Text size="xl" color={theme.colors.text[0]}>
              Data Automation
            </Text>
            <ActionIcon onClick={() => handleClose()}>
              <IconX color={theme.colors.main[9]} />
            </ActionIcon>
          </Flex>
          <Flex gap={8} justify="flex-start">
            <Flex direction="column" w={isSophia ? "70%" : "100%"}>
              <Text>
                Please review and confirm that all auto-filled information is
                correct.
              </Text>
              <Text>
                By clicking "<span style={{ fontWeight: 200 }}>Accept</span>",
                you agree to the use of automated inputs and take responsibility
                for verifying their accuracy.
              </Text>
            </Flex>
            {isSophia && (
              <Flex direction="column" gap={8} w={"20%"} justify="center">
                <Flex gap={8} justify="flex-end" align="center">
                  <Text>Anchor</Text>
                  <Checkbox
                    icon={IconGps}
                    size={"lg"}
                    checked={anchorChecked}
                    onChange={event =>
                      setAnchorChecked(event.currentTarget.checked)
                    }
                  />
                </Flex>
                <Flex gap={8} justify="flex-end" align="center">
                  <Text>Underway</Text>
                  <Checkbox
                    icon={IconGps}
                    size={"lg"}
                    checked={underwayChecked}
                    onChange={event =>
                      setUnderwayChecked(event.currentTarget.checked)
                    }
                  />
                </Flex>
              </Flex>
            )}
          </Flex>
          <Flex justify="center" direction="column" align="center" mt={10}>
            {isLoading ? (
              <Loader />
            ) : (
              <Flex
                direction="column"
                justify="flex-start"
                style={{
                  width: "100%",
                  maxHeight: 500,
                  overflowY: "scroll",
                  overflowX: "hidden",
                }}>
                {previewSections.map(
                  (s, i) =>
                    s.rows.length > 0 && (
                      <Fragment key={`pr-sec-${i}`}>
                        <NMEAAutomationModalPreview
                          section={s}
                          sectionIndex={s.originalSectionIndex}
                          selectedSections={selectedSections}
                          setSelectedSections={setSelectedSections}
                          onlyOneAllowed={
                            NMEAModalData?.sectionIndex !== undefined
                          }
                        />
                      </Fragment>
                    ),
                )}
              </Flex>
            )}
          </Flex>

          <Flex justify="space-between" mt={10}>
            <Button
              onClick={handleAccept}
              loading={loading === "auto"}
              disabled={
                selectedSections.length === 0 ||
                selectedSections.every(s => !s || s.length === 0)
              }>
              Accept
            </Button>
            <Button
              onClick={handleEnterManually}
              loading={loading === "manual"}>
              Enter Manually
            </Button>
          </Flex>
        </StyledNMEAModalContainer>
      </StyledNMEAOverlay>
    </Portal>
  );
};

export default NMEAAutomationModal;
